import { Button } from 'antd';
import dayjs from 'dayjs';
import { createElement } from 'react';
import {
  createExpenseEventTableColumns,
  expenseEventVisibleFields,
  formatExpenseEventDetailValue,
} from './expense-event-fields';
import { formatPlanRatio } from './plan-values';

export const dataLogTabList = [
  { key: 'plan', tab: '经营计划' },
  { key: 'income', tab: '经营收入' },
  { key: 'salary', tab: '门店工资' },
  { key: 'expense', tab: '经营费用' },
  { key: 'payment', tab: '付款数据' },
];

const dataLogTypeNames = {
  expense: '经营费用',
  income: '经营收入',
  payment: '付款数据',
  plan: '经营计划',
  salary: '门店工资',
  stores: '门店数据',
};
const dataLogTypes = new Set(dataLogTabList.map(({ key }) => key));

const storeFields = [
  { dataIndex: 'id', title: '门店ID' },
  { dataIndex: 'operationEntityId', title: '经营实体ID' },
  { dataIndex: 'sourceLogId', title: '来源日志ID' },
  { dataIndex: 'code', title: '门店编码' },
  { dataIndex: 'name', title: '门店名称' },
  { dataIndex: 'aliases', title: '门店别名' },
  { dataIndex: 'businessStatus', title: '营业状态' },
  { dataIndex: 'businessFormat', title: '经营业态' },
  { dataIndex: 'inventoryManagerName', title: '库存负责人' },
  { dataIndex: 'openedAt', title: '开业日期', valueType: 'date' },
  { dataIndex: 'area', title: '经营面积' },
  { dataIndex: 'region', title: '地域' },
  { dataIndex: 'businessAddress', title: '经营地址' },
  { dataIndex: 'createdAt', title: '创建时间', valueType: 'dateTime' },
  { dataIndex: 'updatedAt', title: '更新时间', valueType: 'dateTime' },
];

const planFields = [
  { dataIndex: 'id', title: '计划ID' },
  { dataIndex: 'operationEntityCode', title: '经营实体编码' },
  { dataIndex: 'operationEntityName', title: '经营实体名称' },
  { dataIndex: 'sourceLogId', title: '来源日志ID' },
  { dataIndex: 'planStartedAt', title: '计划开始日期', valueType: 'date' },
  { dataIndex: 'planEndedAt', title: '计划结束日期', valueType: 'date' },
  { dataIndex: 'planItemName', title: '计划项目' },
  { dataIndex: 'planValueType', title: '计划值类型' },
  { dataIndex: 'planAmountCent', title: '计划金额分' },
  { dataIndex: 'planQuantity', title: '计划数量' },
  { dataIndex: 'planRatio', title: '计划比例' },
  { dataIndex: 'createdAt', title: '创建时间', valueType: 'dateTime' },
  { dataIndex: 'updatedAt', title: '更新时间', valueType: 'dateTime' },
];

const incomeFields = [
  { dataIndex: 'id', title: '收入事件ID' },
  { dataIndex: 'sourceLogId', title: '来源日志ID' },
  { dataIndex: 'operationEntityCode', title: '经营实体编码' },
  { dataIndex: 'sourceStoreName', title: '来源门店名称' },
  { dataIndex: 'incomeDate', title: '收入日期', valueType: 'date' },
  { dataIndex: 'incomeTypeLevel1', title: '一级收入类型' },
  { dataIndex: 'incomeTypeLevel2', title: '二级收入类型' },
  { dataIndex: 'incomeTypeLevel3', title: '三级收入类型' },
  { dataIndex: 'incomeValueType', title: '数值类型' },
  { dataIndex: 'incomeAmountCent', title: '金额分' },
  { dataIndex: 'incomeQuantity', title: '数量' },
  { dataIndex: 'incomeRatio', title: '比率' },
  { dataIndex: 'createdAt', title: '创建时间', valueType: 'dateTime' },
  { dataIndex: 'updatedAt', title: '更新时间', valueType: 'dateTime' },
];

const salaryFields = [
  { dataIndex: 'id', title: '工资事件ID' },
  { dataIndex: 'sourceLogId', title: '来源日志ID' },
  { dataIndex: 'operationEntityCode', title: '经营实体编码' },
  { dataIndex: 'sourceStoreName', title: '来源门店名称' },
  { dataIndex: 'salaryStartedAt', title: '工资开始日期', valueType: 'date' },
  { dataIndex: 'salaryEndedAt', title: '工资结束日期', valueType: 'date' },
  { dataIndex: 'salaryTypeLevel1', title: '一级工资类型' },
  { dataIndex: 'salaryTypeLevel2', title: '二级工资类型' },
  { dataIndex: 'salaryTypeLevel3', title: '三级工资类型' },
  { dataIndex: 'salaryValueType', title: '数值类型' },
  { dataIndex: 'salaryAmountCent', title: '金额分' },
  { dataIndex: 'salaryQuantity', title: '数量' },
  { dataIndex: 'salaryRatio', title: '比率' },
  { dataIndex: 'createdAt', title: '创建时间', valueType: 'dateTime' },
  { dataIndex: 'updatedAt', title: '更新时间', valueType: 'dateTime' },
];

const paymentFields = [
  { dataIndex: 'id', title: '事件ID' },
  { dataIndex: 'approvalStatus', title: '审批状态' },
  { dataIndex: 'title', title: '标题' },
  { dataIndex: 'documentNo', title: '付款单号' },
  { dataIndex: 'submitter', title: '提交人' },
  { dataIndex: 'submittedAt', title: '提交日期', valueType: 'date' },
  { dataIndex: 'paidAt', title: '支付日期', valueType: 'date' },
  { dataIndex: 'payeeInfo', title: '收款信息' },
  { dataIndex: 'paidAmountText', title: '支付金额原文' },
  { dataIndex: 'paidAmountCent', title: '支付金额分', valueType: 'digit' },
  { dataIndex: 'paymentMethod', title: '支付方式' },
  { dataIndex: 'payerAccountName', title: '付款账户' },
  { dataIndex: 'description', title: '描述' },
  { dataIndex: 'payPlan', title: '付款计划' },
  { dataIndex: 'documentDetail', title: '单据详情' },
  { dataIndex: 'createdAt', title: '日志创建时间', valueType: 'dateTime' },
  { dataIndex: 'updatedAt', title: '日志更新时间', valueType: 'dateTime' },
];

const detailFields = {
  expense: expenseEventVisibleFields,
  income: incomeFields,
  payment: paymentFields,
  plan: planFields,
  salary: salaryFields,
  stores: storeFields,
};

const dateFormats = {
  createdAt: 'YYYY-MM-DD HH:mm:ss',
  incomeDate: 'YYYY-MM-DD',
  loanDate: 'YYYY-MM-DD',
  openedAt: 'YYYY-MM-DD',
  paidAt: 'YYYY-MM-DD',
  planEndedAt: 'YYYY-MM-DD',
  planStartedAt: 'YYYY-MM-DD',
  repaymentDate: 'YYYY-MM-DD',
  requisitionDate: 'YYYY-MM-DD',
  salaryEndedAt: 'YYYY-MM-DD',
  salaryStartedAt: 'YYYY-MM-DD',
  submittedAt: 'YYYY-MM-DD',
  updatedAt: 'YYYY-MM-DD HH:mm:ss',
};

const createActionColumn = (onViewDetail) => ({
  title: '操作',
  valueType: 'option',
  fixed: 'right',
  width: 96,
  render: (_, record) => [
    createElement(
      Button,
      { key: 'detail', onClick: () => onViewDetail(record), type: 'link' },
      '查看详情',
    ),
  ],
});

const transformDateRange = (startKey, endKey) => (value) => {
  const range = Array.isArray(value) ? value : [];
  const start = range[0] ? dayjs(range[0]) : null;
  const end = range[1] ? dayjs(range[1]) : null;
  return {
    [endKey]: end?.isValid() ? end.format('YYYY-MM-DD') : undefined,
    [startKey]: start?.isValid() ? start.format('YYYY-MM-DD') : undefined,
  };
};

export const createPlanDateRangeColumn = () => ({
  title: '计划周期',
  dataIndex: 'planDateRange',
  valueType: 'dateRange',
  hideInTable: true,
  search: { transform: transformDateRange('planDateStart', 'planDateEnd') },
});

export const createIncomeDateRangeColumn = () => ({
  title: '收入日期',
  dataIndex: 'incomeDateRange',
  valueType: 'dateRange',
  hideInTable: true,
  search: { transform: transformDateRange('incomeDateStart', 'incomeDateEnd') },
});

export const createSalaryDateRangeColumn = () => ({
  title: '工资周期',
  dataIndex: 'salaryDateRange',
  valueType: 'dateRange',
  hideInTable: true,
  search: { transform: transformDateRange('salaryDateStart', 'salaryDateEnd') },
});

export const getDataLogType = (type) => (dataLogTypes.has(type) ? type : 'plan');

export const getDataLogTypeName = (type) => dataLogTypeNames[type] || '数据日志';

export const formatDataLogDetailValue = (field, value) => {
  if (
    ['incomeRatio', 'planRatio', 'salaryRatio'].includes(field.dataIndex) &&
    value !== undefined &&
    value !== null &&
    value !== ''
  ) {
    return formatPlanRatio(value);
  }
  if (field.dataIndex === 'aliases' && Array.isArray(value)) {
    return value.length ? value.join('、') : '-';
  }
  const format = dateFormats[field.dataIndex];
  if (format && value !== undefined && value !== null && value !== '') {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format(format) : value;
  }
  return formatExpenseEventDetailValue(field, value);
};

export const getDataLogDetailItems = (type, record) =>
  (detailFields[type] || detailFields.plan).map((field) => ({
    key: field.dataIndex,
    label: field.title,
    children: formatDataLogDetailValue(field, record?.[field.dataIndex], record),
  }));

const commonColumn = (field, searchable = false) => ({
  ...field,
  search: searchable ? undefined : false,
  width: 150,
});

const createPlanColumns = (onViewDetail) => [
  createPlanDateRangeColumn(),
  commonColumn(
    {
      dataIndex: 'operationEntityName',
      title: '经营实体',
      renderText: (_, record) =>
        [record.operationEntityCode, record.operationEntityName].filter(Boolean).join(' '),
    },
    true,
  ),
  commonColumn({
    dataIndex: 'planStartedAt',
    title: '计划周期',
    renderText: (_, record) =>
      `${formatDataLogDetailValue(
        { dataIndex: 'planStartedAt' },
        record.planStartedAt,
      )} ~ ${formatDataLogDetailValue({ dataIndex: 'planEndedAt' }, record.planEndedAt)}`,
  }),
  commonColumn({ dataIndex: 'planItemName', title: '计划项目' }, true),
  commonColumn(
    {
      dataIndex: 'planValueType',
      title: '计划值类型',
      valueEnum: { amount: '金额', quantity: '数量', ratio: '比率' },
    },
    true,
  ),
  commonColumn({ dataIndex: 'planAmountCent', title: '计划金额分', valueType: 'digit' }),
  commonColumn({ dataIndex: 'planQuantity', title: '计划数量', valueType: 'digit' }),
  commonColumn({
    dataIndex: 'planRatio',
    title: '计划比例',
    renderText: (value, record) =>
      formatDataLogDetailValue({ dataIndex: 'planRatio' }, value, record),
  }),
  createActionColumn(onViewDetail),
];

const createPaymentColumns = (onViewDetail) => [
  commonColumn({ dataIndex: 'title', title: '标题' }, true),
  commonColumn({ dataIndex: 'documentNo', title: '付款单号' }, true),
  commonColumn({ dataIndex: 'approvalStatus', title: '审批状态' }, true),
  commonColumn({ dataIndex: 'paidAt', title: '支付日期', valueType: 'date' }),
  commonColumn({ dataIndex: 'paidAmountCent', title: '支付金额分', valueType: 'digit' }),
  commonColumn({ dataIndex: 'paymentMethod', title: '支付方式' }, true),
  commonColumn({ dataIndex: 'payerAccountName', title: '付款账户' }, true),
  commonColumn({ dataIndex: 'submitter', title: '提交人' }, true),
  createActionColumn(onViewDetail),
];

const createIncomeColumns = (onViewDetail) => [
  createIncomeDateRangeColumn(),
  commonColumn({ dataIndex: 'operationEntityCode', title: '经营实体编码' }, true),
  commonColumn({ dataIndex: 'sourceStoreName', title: '来源门店名称' }, true),
  commonColumn({ dataIndex: 'incomeDate', title: '收入日期', valueType: 'date' }),
  commonColumn({ dataIndex: 'incomeTypeLevel1', title: '一级收入类型' }, true),
  commonColumn({ dataIndex: 'incomeTypeLevel2', title: '二级收入类型' }, true),
  commonColumn({ dataIndex: 'incomeTypeLevel3', title: '三级收入类型' }, true),
  commonColumn(
    {
      dataIndex: 'incomeValueType',
      title: '数值类型',
      valueEnum: { amount: '金额', quantity: '数量', ratio: '比率' },
    },
    true,
  ),
  commonColumn({ dataIndex: 'incomeAmountCent', title: '金额分', valueType: 'digit' }),
  commonColumn({ dataIndex: 'incomeQuantity', title: '数量', valueType: 'digit' }),
  commonColumn({
    dataIndex: 'incomeRatio',
    title: '比率',
    renderText: (value, record) =>
      formatDataLogDetailValue({ dataIndex: 'incomeRatio' }, value, record),
  }),
  createActionColumn(onViewDetail),
];

const createSalaryColumns = (onViewDetail) => [
  createSalaryDateRangeColumn(),
  commonColumn({ dataIndex: 'operationEntityCode', title: '经营实体编码' }, true),
  commonColumn({ dataIndex: 'sourceStoreName', title: '来源门店名称' }, true),
  commonColumn({
    dataIndex: 'salaryStartedAt',
    title: '工资周期',
    renderText: (_, record) =>
      `${formatDataLogDetailValue(
        { dataIndex: 'salaryStartedAt' },
        record.salaryStartedAt,
      )} ~ ${formatDataLogDetailValue({ dataIndex: 'salaryEndedAt' }, record.salaryEndedAt)}`,
  }),
  commonColumn({ dataIndex: 'salaryTypeLevel1', title: '一级工资类型' }, true),
  commonColumn({ dataIndex: 'salaryTypeLevel2', title: '二级工资类型' }, true),
  commonColumn({ dataIndex: 'salaryTypeLevel3', title: '三级工资类型' }, true),
  commonColumn(
    {
      dataIndex: 'salaryValueType',
      title: '数值类型',
      valueEnum: { amount: '金额', quantity: '数量', ratio: '比率' },
    },
    true,
  ),
  commonColumn({ dataIndex: 'salaryAmountCent', title: '金额分', valueType: 'digit' }),
  commonColumn({ dataIndex: 'salaryQuantity', title: '数量', valueType: 'digit' }),
  commonColumn({
    dataIndex: 'salaryRatio',
    title: '比率',
    renderText: (value, record) =>
      formatDataLogDetailValue({ dataIndex: 'salaryRatio' }, value, record),
  }),
  createActionColumn(onViewDetail),
];

export const createDataLogColumns = (type, onViewDetail) => {
  switch (getDataLogType(type)) {
    case 'income':
      return createIncomeColumns(onViewDetail);
    case 'salary':
      return createSalaryColumns(onViewDetail);
    case 'expense':
      return createExpenseEventTableColumns(onViewDetail);
    case 'payment':
      return createPaymentColumns(onViewDetail);
    default:
      return createPlanColumns(onViewDetail);
  }
};
